import axios from "axios";

// Always send session cookie for cross-origin requests (used by Emergent Google Auth)
axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

export const api = {
  // Auth
  authMe: () => client.get("/auth/me").then((r) => r.data),
  authGoogleCallback: (session_id, invite_code) =>
    client.post("/auth/google/callback", { session_id, ...(invite_code ? { invite_code } : {}) }).then((r) => r.data),
  authGoogleCompleteInvite: (pending_id, invite_code) =>
    client.post("/auth/google/complete-invite", { pending_id, invite_code }).then((r) => r.data),
  authLogout: () => client.post("/auth/logout").then((r) => r.data),

  // Admin — Invite codes
  adminListInvites: () => client.get("/invites").then((r) => r.data),
  adminCreateInvite: (data) => client.post("/invites", data).then((r) => r.data),
  adminToggleInvite: (code, active) => client.patch(`/invites/${encodeURIComponent(code)}`, { active }).then((r) => r.data),
  adminDeleteInvite: (code) => client.delete(`/invites/${encodeURIComponent(code)}`).then((r) => r.data),
  // Products
  listProducts: (params = {}) => client.get("/products", { params }).then((r) => r.data),
  getProduct: (id) => client.get(`/products/${id}`).then((r) => r.data),
  createProduct: (data) => client.post("/products", data).then((r) => r.data),
  updateProduct: (id, data) => client.put(`/products/${id}`, data).then((r) => r.data),
  deleteProduct: (id) => client.delete(`/products/${id}`).then((r) => r.data),
  importProducts: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  previewProductsImport: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/products/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  exportProducts: (format = "csv") =>
    client.get("/products/export", { params: { format }, responseType: "blob" }),
  listCategories: () => client.get("/categories").then((r) => r.data),

  // Sales
  listSales: (limit = 500) => client.get("/sales", { params: { limit } }).then((r) => r.data),
  createSale: (data) => client.post("/sales", data).then((r) => r.data),
  deleteSale: (id) => client.delete(`/sales/${id}`).then((r) => r.data),
  importSales: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/sales/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  exportSales: (format = "csv", days = null) =>
    client.get("/sales/export", { params: { format, ...(days ? { days } : {}) }, responseType: "blob" }),
  salesAnalytics: (days = 30) => client.get("/sales/analytics", { params: { days } }).then((r) => r.data),
  kpiDetail: (metric, days = 30, comparison = "previous_period") =>
    client.get("/analytics/kpi-detail", { params: { metric, days, comparison } }).then((r) => r.data),

  // Alerts + Dashboard
  getAlerts: () => client.get("/alerts").then((r) => r.data),
  dashboardSummary: () => client.get("/dashboard/summary").then((r) => r.data),

  // Settings
  getNotificationSettings: () => client.get("/settings/notifications").then((r) => r.data),
  updateNotificationSettings: (data) => client.put("/settings/notifications", data).then((r) => r.data),
  patchNotificationSettings: (data) => client.patch("/settings/notifications", data).then((r) => r.data),

  // Hints
  getShelfLifeHints: (category, name) => client.get("/hints/shelf-life", { params: { category, name } }).then((r) => r.data),

  // Matching
  matchProducts: (data) => client.post("/products/match", data).then((r) => r.data),
  adjustStock: (id, data) => client.post(`/products/${id}/adjust-stock`, data).then((r) => r.data),
  deleteLot: (productId, lotId) => client.delete(`/products/${productId}/lots/${lotId}`).then((r) => r.data),
  updateLot: (productId, lotId, data) => client.patch(`/products/${productId}/lots/${lotId}`, data).then((r) => r.data),

  // Expiry scan
  scanExpiry: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/scan/expiry", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }).then((r) => r.data);
  },

  // Suppliers
  listSuppliers: () => client.get("/suppliers").then((r) => r.data),
  getSupplier: (id) => client.get(`/suppliers/${id}`).then((r) => r.data),
  createSupplier: (data) => client.post("/suppliers", data).then((r) => r.data),
  updateSupplier: (id, data) => client.put(`/suppliers/${id}`, data).then((r) => r.data),
  deleteSupplier: (id) => client.delete(`/suppliers/${id}`).then((r) => r.data),
  mergeSuppliers: (winnerId, losers) => client.post(`/suppliers/${winnerId}/merge`, { losers }).then((r) => r.data),
  assignSupplier: (productIds, supplierId) =>
    client.post(`/products/assign-supplier`, { product_ids: productIds, supplier_id: supplierId }).then((r) => r.data),
  matchSupplierByName: (name) => client.get("/suppliers/match/by-name", { params: { name } }).then((r) => r.data),

  // Reorder
  getReorderSuggestions: () => client.get("/reorder/suggestions").then((r) => r.data),

  // Notifications
  sendTestNotification: (data = {}) => client.post("/notifications/test", data, { timeout: 30000 }).then((r) => r.data),
  listNotificationLogs: (limit = 30) => client.get("/notifications/logs", { params: { limit } }).then((r) => r.data),

  // Demo
  resetDemo: (confirmation, seed = true) =>
    client.post("/demo/reset", { confirmation, seed }).then((r) => r.data),
  resetPreview: () => client.get("/demo/reset/preview").then((r) => r.data),

  // Onboarding tour
  onboardingStatus: () => client.get("/onboarding/status").then((r) => r.data),
  onboardingComplete: () => client.post("/onboarding/complete").then((r) => r.data),
  onboardingRestart: () => client.post("/onboarding/restart").then((r) => r.data),

  // Account (GDPR)
  exportAccount: () => client.get("/account/export").then((r) => r.data),
  deleteAccount: () => client.delete("/account/me").then((r) => r.data),

  // Team / Store
  teamMe: () => client.get("/team/me").then((r) => r.data),
  teamMyStores: () => client.get("/team/stores").then((r) => r.data),
  teamMembers: () => client.get("/team/members").then((r) => r.data),
  teamSwitch: (storeId) => client.post(`/team/switch/${storeId}`).then((r) => r.data),
  teamUpdatePermissions: (memberUserId, permissions) =>
    client.put(`/team/members/${memberUserId}/permissions`, { permissions }).then((r) => r.data),
  teamRemoveMember: (memberUserId) => client.delete(`/team/members/${memberUserId}`).then((r) => r.data),
  teamCreateInvite: (data = {}) => client.post("/team/invite", data).then((r) => r.data),
  teamJoin: (code) => client.post("/team/join", { code }).then((r) => r.data),
  teamUpdateStore: (data) => client.put("/team/store", data).then((r) => r.data),

  // Billing / Stripe
  createCheckoutSession: (storeId) =>
    client.post(`/stores/${encodeURIComponent(storeId)}/checkout-session`).then((r) => r.data),

  // Shelf-life learnings (per-store AI override based on user confirmations)
  confirmShelfLifeLearnings: (confirmations) =>
    client.post("/learnings/shelf-life/batch", { confirmations }).then((r) => r.data),
  listShelfLifeLearnings: () => client.get("/learnings/shelf-life").then((r) => r.data),
  deleteShelfLifeLearning: (nameNormalized) =>
    client.delete(`/learnings/shelf-life/${encodeURIComponent(nameNormalized)}`).then((r) => r.data),
};
